import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Sparkles, Edit3, Save, X, ChevronDown, ChevronRight, MessageSquare, Send, RefreshCw, AlertTriangle } from 'lucide-react'
import { getProjectAnalysis, requestProjectAnalysis, updateProjectAnalysis, submitProjectClarifyingAnswers, reprocessProject, getReprocessStatus, updateProject } from '../../services/api'
import type { ProjectAIAnalysis, ClarifyingQuestion, ReprocessJob, Project, Document } from '../../types'
import type { JournalArticle } from '../../services/api'
import MoveDocumentModal from './MoveDocumentModal'
import ScoreDisplay from '../shared/ScoreDisplay'
import PillDrillDownPanel from './PillDrillDownPanel'

interface ProjectAIAnalysisPanelProps {
  projectId: string
  inventionDeclaration?: string
  /** The declaration exists but cannot be decrypted with the current key (UX-022). */
  declarationUnreadable?: boolean
  onAddDeclaration?: () => void
  project?: Project
  documents?: Document[]
  priorArt?: JournalArticle[]
  /** F15: the parent's poll gave up waiting (its own 5-minute cap) without the
   * backend ever reaching a terminal status. Renders a timeout/retry state
   * instead of leaving the "Analysing..." spinner running forever with no
   * way to tell a genuinely slow job from a silently dead one. */
  analysisTimedOut?: boolean
  /** UX-054: when the project's Assessment has been scored, the date it was scored
   * (already formatted). The empty state then says what exists instead of
   * "No AI analysis generated yet" beside a scored Assessment. */
  assessmentScoredOn?: string
}

export default function ProjectAIAnalysisPanel({ projectId, inventionDeclaration, declarationUnreadable = false, onAddDeclaration, project, documents = [], priorArt = [], analysisTimedOut, assessmentScoredOn }: ProjectAIAnalysisPanelProps) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [selectedPill, setSelectedPill] = useState<{ term: string; kind: 'theme' | 'novel' | 'overlap' } | null>(null)
  const [editSummary, setEditSummary] = useState('')
  const [editThemes, setEditThemes] = useState<string[]>([])
  const [editNovelAspects, setEditNovelAspects] = useState<string[]>([])
  const [editOverlapAreas, setEditOverlapAreas] = useState<string[]>([])
  const [showHistory, setShowHistory] = useState(false)
  // BUG-NEW-L (Task #2713): clarifyAnswers is drafted in local component
  // state. Previously it was lost on navigation — the user would lose
  // their in-progress answers to clarifying questions.
  //
  // Persistence strategy: hydrate from localStorage on mount, write-through
  // on each change. Scoped by projectId so answers don't bleed between
  // projects. Cleared once the user explicitly Submits (which promotes the
  // drafts to persisted clarifying_questions.answer via the backend
  // /clarify endpoint).
  //
  // Follow-up: full cross-device draft persistence needs a backend
  // /novelty/project/{id}/clarify/draft endpoint; localStorage covers the
  // in-session / same-browser navigation case that Phase 1 flagged.
  const draftKey = `innovateflow:clarify-draft:${projectId}`
  const [clarifyAnswers, setClarifyAnswers] = useState<Record<string, string>>(() => {
    if (typeof window === 'undefined') return {}
    try {
      const raw = window.localStorage.getItem(draftKey)
      return raw ? (JSON.parse(raw) as Record<string, string>) : {}
    } catch {
      return {}
    }
  })
  // Write-through: persist drafts to localStorage whenever they change.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      if (Object.keys(clarifyAnswers).length === 0) {
        window.localStorage.removeItem(draftKey)
      } else {
        window.localStorage.setItem(draftKey, JSON.stringify(clarifyAnswers))
      }
    } catch {
      /* quota / serialization error — draft autosave is best-effort */
    }
  }, [clarifyAnswers, draftKey])
  const [showReprocessConfirm, setShowReprocessConfirm] = useState(false)
  const [reprocessJob, setReprocessJob] = useState<ReprocessJob | null>(null)
  const [showMoveModal, setShowMoveModal] = useState(false)
  // UX-054: Generate costs money and writes saved data, so it asks first.
  const [confirmGenerate, setConfirmGenerate] = useState(false)

  const { data: analysis, isLoading } = useQuery({
    queryKey: ['project-analysis', projectId],
    queryFn: () => getProjectAnalysis(projectId),
    enabled: !!projectId,
  })

  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const generateMutation = useMutation({
    mutationFn: () => requestProjectAnalysis(projectId),
    onSuccess: () => {
      setAnalysisError(null)
      queryClient.invalidateQueries({ queryKey: ['project-analysis', projectId] })
    },
    onError: (err: any) => {
      // DB-1 (UX review 20 Jun): do NOT present a hard failure as a transient
      // delay. Surface the backend's sanitised, honest message (e.g. a service
      // configuration issue) rather than claiming the system "will continue
      // processing" when it has already stopped.
      const backendMsg = err?.response?.data?.error
      setAnalysisError(backendMsg || 'AI analysis could not be completed. Please try again.')
    },
  })

  const saveMutation = useMutation({
    mutationFn: (corrections: Partial<ProjectAIAnalysis>) => updateProjectAnalysis(projectId, corrections),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-analysis', projectId] })
      setEditing(false)
    },
  })

  const clarifyMutation = useMutation({
    mutationFn: (answers: ClarifyingQuestion[]) => submitProjectClarifyingAnswers(projectId, answers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-analysis', projectId] })
      // BUG-NEW-L: clear both local state and the persisted draft so the
      // submitted answers don't replay after a refresh.
      setClarifyAnswers({})
      if (typeof window !== 'undefined') {
        try { window.localStorage.removeItem(draftKey) } catch { /* ignore */ }
      }
    },
  })

  const startEdit = () => {
    if (!analysis) return
    setEditSummary(analysis.summary)
    setEditThemes([...analysis.key_themes])
    setEditNovelAspects([...analysis.novel_aspects])
    // Coerce object-shaped overlap_areas to plain strings for the textarea.
    setEditOverlapAreas(analysis.overlap_areas.map((a) => (typeof a === 'string' ? a : a?.overlap_description ?? '')).filter(Boolean))
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
  }

  const saveCorrections = () => {
    saveMutation.mutate({
      summary: editSummary,
      key_themes: editThemes,
      novel_aspects: editNovelAspects,
      overlap_areas: editOverlapAreas,
    })
  }

  const submitAnswers = () => {
    if (!analysis?.clarifying_questions) return
    const answered = analysis.clarifying_questions.map((q) => ({
      ...q,
      answer: clarifyAnswers[q.id] || '',
      answered_at: new Date().toISOString(),
    }))
    clarifyMutation.mutate(answered)
  }

  const reprocessMutation = useMutation({
    mutationFn: () => reprocessProject(projectId),
    onSuccess: (job) => {
      setReprocessJob(job)
      setShowReprocessConfirm(false)
    },
  })

  // Poll reprocess job status. BUG-H: a single failed poll (a 504 from a slow
  // Lambda, or a brief network blip) must NOT permanently freeze the progress UI.
  // Previously one error called clearInterval and the spinner stuck forever at the
  // last count. Now we retry and only give up after several consecutive failures;
  // the backend watchdog force-completes a genuinely stuck job after the stale
  // threshold, so polling always terminates on its own.
  useEffect(() => {
    if (!reprocessJob || reprocessJob.status === 'completed' || reprocessJob.status === 'failed') return
    let consecutiveErrors = 0
    const interval = setInterval(async () => {
      try {
        const status = await getReprocessStatus(projectId, reprocessJob.job_id)
        consecutiveErrors = 0
        setReprocessJob(status)
        if (status.status === 'completed') {
          queryClient.invalidateQueries({ queryKey: ['project-analysis', projectId] })
          queryClient.invalidateQueries({ queryKey: ['project-prior-art', projectId] })
          clearInterval(interval)
        }
        if (status.status === 'failed') clearInterval(interval)
      } catch {
        consecutiveErrors += 1
        // Give up only after ~30s of continuous failure, not on the first blip.
        if (consecutiveErrors >= 6) clearInterval(interval)
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [reprocessJob, projectId, queryClient])

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
      </div>
    )
  }

  if (!analysis) {
    const analysisStatus = project?.ai_analysis_status
    const backendError = project?.ai_analysis_error

    // P3-9: a backend analysis that genuinely FAILED must surface the real error
    // and a Retry — not the misleading "No analysis yet" / "taking longer" state.
    if (analysisStatus === 'failed') {
      return (
        <div className="text-center py-8" data-testid="analysis-failed">
          <AlertTriangle className="w-10 h-10 mx-auto text-red-400 mb-3" />
          <p className="text-sm font-medium text-red-700 mb-1">AI analysis failed</p>
          <p className="text-sm text-gray-600 mb-4">
            {backendError || 'The analysis could not be completed. You can retry it now.'}
          </p>
          {analysisError && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              {analysisError}
            </div>
          )}
          <button
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending || !inventionDeclaration}
            className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50"
          >
            {generateMutation.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Retrying...</>
            ) : (
              <><RefreshCw className="w-4 h-4" /> Retry Analysis</>
            )}
          </button>
        </div>
      )
    }

    // P3-9: analysis still running — an honest "processing" state (the parent
    // polls while pending/in_progress, so this resolves to a result or a failure).
    if (analysisStatus === 'pending' || analysisStatus === 'in_progress') {
      // F15: the parent's poll already gave up (its own 5-minute cap) without
      // the backend ever reaching a terminal status. Rather than leave this
      // spinning forever with no way to tell "genuinely slow" from "silently
      // dead", say so plainly and offer the same Retry the failed-state above
      // already has.
      if (analysisTimedOut) {
        return (
          <div className="text-center py-8" data-testid="analysis-timed-out">
            <AlertTriangle className="w-10 h-10 mx-auto text-amber-400 mb-3" />
            <p className="text-sm font-medium text-gray-800 mb-1">This is taking longer than expected</p>
            <p className="text-sm text-gray-600 mb-4">
              Analysis has been running for over 5 minutes without finishing. It may still complete in the
              background — you can wait and refresh later, or retry now.
            </p>
            <button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending || !inventionDeclaration}
              className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50"
            >
              {generateMutation.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Retrying...</>
              ) : (
                <><RefreshCw className="w-4 h-4" /> Retry Analysis</>
              )}
            </button>
          </div>
        )
      }
      return (
        <div className="text-center py-8" data-testid="analysis-processing">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-iflow-500 mb-3" />
          <p className="text-sm text-gray-600 mb-1">Analysing your project…</p>
          <p className="text-xs text-gray-500">This usually takes under a minute and updates automatically.</p>
        </div>
      )
    }

    return (
      <div className="text-center py-8">
        <Sparkles className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <p className="text-sm text-gray-500 mb-4" data-testid="analysis-empty-line">
          {assessmentScoredOn
            ? `Assessment scored ${assessmentScoredOn}; the written AI analysis has not been generated.`
            : 'No AI analysis generated yet for this project.'}
        </p>
        {declarationUnreadable && (
          <div data-testid="analysis-declaration-locked" className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Your project description is locked: it is encrypted and your current key cannot open it. Check your key access before generating analysis.
          </div>
        )}
        {!inventionDeclaration && !declarationUnreadable && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Add your project description from ⋯ More before generating analysis. This anchors the AI to your core concept and prevents inaccurate results.
          </div>
        )}
        {analysisError && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            {analysisError}
          </div>
        )}
        {confirmGenerate ? (
          <div role="alertdialog" aria-label="Generate AI analysis" data-testid="generate-confirm" className="mx-auto mb-2 max-w-md rounded-lg border border-amber-200 bg-amber-50 p-3 text-left text-sm text-amber-900">
            <p className="font-semibold">Generate the written AI analysis?</p>
            <p className="mt-1">
              This runs paid AI processing across your documents and saves a new analysis to this
              project. Only continue if you want that cost and that change to saved data.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => { setConfirmGenerate(false); generateMutation.mutate() }}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700"
              >
                <Sparkles className="w-4 h-4" /> Yes, generate (uses AI credits)
              </button>
              <button
                onClick={() => setConfirmGenerate(false)}
                className="px-3 py-1.5 rounded-lg border border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setConfirmGenerate(true)}
            disabled={generateMutation.isPending || !inventionDeclaration}
            className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50"
          >
            {generateMutation.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Generate AI Analysis</>
            )}
          </button>
        )}
      </div>
    )
  }

  const hasDeclaration = !!inventionDeclaration

  // Wave 2 Stream 1: flagged-themes CTA banner conditions
  const showFlaggedBanner =
    (analysis.flagged_themes?.filter((ft: { similarity: number }) => ft.similarity >= 3).length ?? 0) >= 3 &&
    !project?.flagged_themes_acknowledged_at
  const flaggedDocumentIds = analysis.flagged_themes?.map((ft: { document_id?: string }) => ft.document_id).filter(Boolean) as string[] ?? []

  const handleConfirmMatch = async () => {
    await updateProject(projectId, { flagged_themes_acknowledged_at: new Date().toISOString() })
    queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  // BUG-RETEST-E STREAM10-DEDUP: belt-and-braces frontend dedup.
  // The backend _apply_declaration_gate already removes flagged items from
  // novel_aspects/key_themes, but stored records from before that gate was
  // deployed may still contain duplicates. Filter here as a second safety net.
  const _flaggedSet = new Set((analysis.flagged_themes || []).map((ft: { theme: string }) => ft.theme))
  const safeKeyThemes: string[] = (analysis.key_themes || []).filter((t: string) => !_flaggedSet.has(t))
  const safeNovelAspects: string[] = (analysis.novel_aspects || []).filter((a: string) => !_flaggedSet.has(a))
  const sortedKeyThemes = [...safeKeyThemes].sort((a, b) => {
    const summaryLower = (analysis.summary ?? '').toLowerCase()
    const scoreOf = (t: string) => {
      const escaped = t.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return (summaryLower.match(new RegExp(escaped, 'g')) ?? []).length
    }
    return scoreOf(b) - scoreOf(a)
  })
  return (
    <div className="space-y-4">
      {/* STORY-001: Privacy reassurance notice */}
      <div className="flex items-start gap-2 p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
        <svg className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-xs text-blue-700">
          This analysis is visible to you and to anyone you have shared this invention with. Your documents are encrypted in your Australian region and used to generate this analysis and content-derived embeddings for matching. Access to your underlying documents is tightly controlled, logged, and audited.
        </p>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {hasDeclaration ? (
            <ScoreDisplay score={analysis.confidence_score} variant="confidence" />
          ) : (
            <span className="px-2 py-1 text-sm rounded-full font-medium bg-amber-100 text-amber-700">Preliminary</span>
          )}
          <span className="px-2 py-1 text-xs bg-sky-100 text-sky-700 rounded-full">{analysis.domain_classification}</span>
        </div>
        {!editing ? (
          <button onClick={startEdit} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Edit3 className="w-3.5 h-3.5" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={saveCorrections} disabled={saveMutation.isPending} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-iflow-600 text-white rounded-lg hover:bg-iflow-700 disabled:opacity-50">
              {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Corrections
            </button>
            <button onClick={cancelEdit} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        )}
      </div>

      {/* Summary */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-1">Summary</h4>
        {editing ? (
          <textarea
            value={editSummary}
            onChange={(e) => setEditSummary(e.target.value)}
            className="w-full p-2 text-sm light-input"
            rows={4}
          />
        ) : (
          <p className="text-sm text-gray-600">{analysis.summary}</p>
        )}
      </div>

      {/* Key Themes */}
      {(
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-1">Key Themes</h4>
        {editing ? (
          <textarea
            value={editThemes.join('\n')}
            onChange={(e) => setEditThemes(e.target.value.split('\n').filter(Boolean))}
            className="w-full p-2 text-sm light-input"
            rows={3}
            placeholder="One theme per line"
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            {sortedKeyThemes.map((theme, i) => (
              <button
                key={i}
                data-testid={`pill-theme-${i}`}
                onClick={() => setSelectedPill({ term: theme, kind: 'theme' })}
                className="px-2 py-1 text-xs bg-purple-50 text-purple-700 rounded-full hover:bg-purple-100 hover:ring-1 hover:ring-purple-300 transition-colors cursor-pointer"
              >
                {theme}
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Wave 2 Stream 1: Flagged-themes CTA banner — shown when >=3 flagged themes and not yet acknowledged */}
      {showFlaggedBanner && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="flagged-themes-banner">
          <div className="flex items-start gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              Some of your documents may not match this project's declared invention.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowMoveModal(true)}
              className="px-3 py-1.5 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
            >
              Move documents
            </button>
            <button
              onClick={() => onAddDeclaration?.()}
              className="px-3 py-1.5 text-xs bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-lg font-medium transition-colors"
            >
              Edit declaration
            </button>
            <button
              onClick={handleConfirmMatch}
              className="px-3 py-1.5 text-xs bg-white hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-lg font-medium transition-colors"
            >
              Confirm match (suppress this warning)
            </button>
          </div>
        </div>
      )}

      {/* Flagged Themes — themes with low similarity to invention declaration */}
      {analysis.flagged_themes && analysis.flagged_themes.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-medium text-amber-800">
              Flagged Themes ({analysis.flagged_themes.length})
            </h4>
            <span className="text-xs text-amber-600">Low similarity to invention declaration</span>
          </div>
          <div className="space-y-2">
            {analysis.flagged_themes.map((ft, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-white rounded border border-amber-100">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">{ft.theme}</span>
                  <span className="text-xs text-gray-500">({Math.round(ft.similarity * 100)}% match)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Novel Aspects */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-1">Novel Aspects</h4>
        {editing ? (
          <textarea
            value={editNovelAspects.join('\n')}
            onChange={(e) => setEditNovelAspects(e.target.value.split('\n').filter(Boolean))}
            className="w-full p-2 text-sm light-input"
            rows={3}
            placeholder="One aspect per line"
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            {safeNovelAspects.map((aspect, i) => (
              <button
                key={i}
                data-testid={`pill-novel-${i}`}
                onClick={() => setSelectedPill({ term: aspect, kind: 'novel' })}
                className="px-2 py-1 text-xs bg-teal-50 text-teal-700 rounded-full hover:bg-teal-100 hover:ring-1 hover:ring-teal-300 transition-colors cursor-pointer"
              >
                {aspect}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Overlap Areas */}
      {analysis.overlap_areas.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-1">Overlap Areas</h4>
          {editing ? (
            <textarea
              value={editOverlapAreas.join('\n')}
              onChange={(e) => setEditOverlapAreas(e.target.value.split('\n').filter(Boolean))}
              className="w-full p-2 text-sm light-input"
              rows={3}
              placeholder="One area per line"
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {analysis.overlap_areas.map((area, i) => {
                const text = typeof area === 'string' ? area : area?.overlap_description ?? ''
                const severity = typeof area === 'string' ? null : area?.overlap_severity
                if (!text) return null
                return (
                  <button
                    key={i}
                    data-testid={`pill-overlap-${i}`}
                    onClick={() => setSelectedPill({ term: text, kind: 'overlap' })}
                    className="px-2 py-1 text-xs bg-amber-50 text-amber-700 rounded-full hover:bg-amber-100 hover:ring-1 hover:ring-amber-300 transition-colors cursor-pointer"
                    title={severity ? `Severity: ${severity}` : undefined}
                  >
                    {text}
                    {severity ? <span className="ml-1 text-amber-400">({severity})</span> : null}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Clarifying Questions */}
      {analysis.clarifying_questions.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-medium text-amber-800">Clarifying Questions</h4>
          </div>
          <div className="space-y-3">
            {analysis.clarifying_questions.filter((q) => !q.answer).map((q) => (
              <div key={q.id}>
                <p className="text-sm text-amber-900 mb-1">{q.question}</p>
                <input
                  type="text"
                  value={clarifyAnswers[q.id] || ''}
                  onChange={(e) => setClarifyAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  placeholder="Your answer..."
                  className="w-full p-2 text-sm text-gray-900 border border-amber-200 rounded focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                />
              </div>
            ))}
          </div>
          <button
            onClick={submitAnswers}
            disabled={clarifyMutation.isPending}
            className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {clarifyMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Submit Answers
          </button>
        </div>
      )}

      {/* Correction History */}
      {analysis.correction_history.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            {showHistory ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Correction History ({analysis.correction_history.length})
          </button>
          {showHistory && (
            <div className="mt-2 space-y-2">
              {analysis.correction_history.map((entry, i) => (
                <div key={i} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{new Date(entry.timestamp).toLocaleString()}</span>
                    <span className="ml-2">Changed: {entry.changed_fields.join(', ')}</span>
                  </div>
                  {/* BUG-NEW-H: allow refining a past correction by reopening the edit form */}
                  <button
                    onClick={startEdit}
                    disabled={editing}
                    title="Edit this correction — refines the current analysis"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-iflow-700 hover:text-iflow-800 hover:bg-iflow-50 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reprocess All */}
      <div className="border-t border-gray-100 pt-4 mt-4">
        {reprocessJob && reprocessJob.status !== 'completed' && reprocessJob.status !== 'failed' ? (
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Reprocessing in progress</p>
              <p className="text-xs text-amber-600">
                {reprocessJob.completed_docs}/{reprocessJob.total_docs} documents completed
              </p>
            </div>
          </div>
        ) : reprocessJob?.status === 'failed' ? (
          <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-800">Reprocessing failed. Please try again.</p>
          </div>
        ) : (
          <button
            onClick={() => setShowReprocessConfirm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
          >
            <RefreshCw className="w-4 h-4" /> Re-evaluate & Reprocess All
          </button>
        )}
      </div>

      {/* Reprocess Confirmation Dialog */}
      {showReprocessConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowReprocessConfirm(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              <h3 className="text-lg font-semibold text-gray-900">Reprocess All Documents</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              This will clear all auto-generated connections, suggestions, and low-confidence CRM items,
              then reprocess all documents in this project. User-saved items will be preserved.
              This may take several minutes.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowReprocessConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => reprocessMutation.mutate()}
                disabled={reprocessMutation.isPending}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {reprocessMutation.isPending ? 'Starting...' : 'Reprocess All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generated timestamp + version */}
      <div className="flex items-center gap-2">
        <p className="text-xs text-gray-500">Generated {new Date(analysis.generated_at).toLocaleString()}</p>
        {analysis.version_number && (
          <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">
            v{analysis.version_number}
          </span>
        )}
      </div>

      {/* Move Documents modal — triggered from CTA banner */}
      {showMoveModal && (
        <MoveDocumentModal
          documentIds={flaggedDocumentIds}
          currentFolderId={null}
          projectId={projectId}
          onClose={() => setShowMoveModal(false)}
        />
      )}

      {/* Pill drill-down panel — slides in from right when a pill is clicked */}
      {selectedPill && (
        <PillDrillDownPanel
          term={selectedPill.term}
          kind={selectedPill.kind}
          documents={documents}
          priorArt={priorArt}
          onClose={() => setSelectedPill(null)}
        />
      )}
    </div>
  )
}
